import React, { useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom';
import SimulateService from '../../../service/SimulateService';
import { getDefaultNodesByCategory } from '../components/center/modals/remote_connect_modal/utils';
import { ComponentType, RemoteComponentsType, RemoteEdgeType, RemoteNodeType } from '../types';
import { HandleType, Position } from '@xyflow/react';
import { showToastMessage } from '../../../utils/commonFunctions';
import useHwil from '../hooks/useHwil';
import useScenario from '../hooks/useScenario';
import { BusCompSvg, PayloadCompSvg } from '../components/center/modals/remote_connect_modal/Svgs';
import useSatellite from '../hooks/useSatellite';

interface HwilProviderProps {
    children: React.ReactNode;
}

const HwilProvider = ({ children }: HwilProviderProps) => {
    const { scenario } = useScenario();
    const { selected_satellite } = useSatellite()
    const { mission_id, scenario_id, dashboard } = useParams();
    const { setRemoteAppLoader, setAllRemoteApplications, setRemoteConnectionStatus } = useHwil();
    const connectionIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const downloadAsset = async (file: any) => {
        const credentials = sessionStorage.getItem("authentication");
        const fileId: string = file.uniqueId
        try {
            const response = SimulateService.downloadHwilConfigFile(mission_id, scenario_id, fileId, credentials)
            window.location.href = response;
        } catch (error) {
            showToastMessage('error', 'Failed to download asset')
        }
    }

    const set_all_remote_components = useCallback((components: RemoteComponentsType) => {
        setAllRemoteApplications(components)
    }, [])

    const setRemoteComponents = (components: RemoteNodeType[], remote_applications: ComponentType[]) => {
        const allUpdatedComponents = components?.map(comp => {
            const matchedApp = remote_applications.find(app => app?.uniqueId === comp?.data?.uniqueId);
            return matchedApp
                ? {
                    ...comp,
                    data: {
                        ...comp.data,
                        selected: true,
                        status: matchedApp.status,
                        componentType: 'HWIL',
                        onDownload: () => downloadAsset(matchedApp)
                    }
                }
                : {
                    ...comp,
                    data: {
                        ...comp.data,
                        selected: false,
                        componentType: 'SWIL',
                    }
                };
        })
        const Edges: RemoteEdgeType[] = [
            { id: '1-2', source: '1', sourceHandle: 'left', target: '2', targetHandle: 'top', type: 'smoothstep' },
            { id: '1-3', source: '1', sourceHandle: 'right', target: '3', targetHandle: 'top', type: 'smoothstep' },
        ];

        allUpdatedComponents?.length > 3 && allUpdatedComponents.map(app => {
            if (app.id.includes('payload')) {
                Edges.push({ id: `2-${app.id}`, sourceHandle: 'bottom', source: '2', targetHandle: 'left', target: app.id, type: 'smoothstep', })
            } else if (app.id.includes('bus')) {
                Edges.push({ id: `3-${app.id}`, sourceHandle: 'bottom', source: '3', targetHandle: 'right', target: app.id, type: 'smoothstep', })
            }
        })
        set_all_remote_components({
            nodes: allUpdatedComponents,
            edges: Edges,
        });
    }

    const getAllRemoteComponents = async () => {
        setRemoteAppLoader(true)
        try {
            if (!mission_id || !scenario_id || !dashboard || !selected_satellite || scenario?.status !== 'SimulationRunning' || (scenario.scenarioDeploymentType !== 'HARDWARE_IN_LOOP' && scenario.scenarioDeploymentType !== 'QUICK_DEPLOY')) return
            const components_response = await SimulateService.getHWILComponents(mission_id, scenario_id)
            const payloadComponents = components_response?.data?.data?.payload || [];
            const busComponents = components_response?.data?.data?.bus || [];

            const defaultComponents: RemoteNodeType[] = getDefaultNodesByCategory(['Payload', 'Bus'], selected_satellite?.label);

            const updatedPayloadComponents: RemoteNodeType[] = payloadComponents.map((component: any, index: number) => ({
                id: `payload-${index + 1}`,
                data: {
                    label: component?.name,
                    category: component?.category,
                    icon: <PayloadCompSvg />,
                    handlers: [
                        {
                            id: 'left',
                            type: 'target' as HandleType,
                            position: Position.Left,
                        }
                    ],
                    disabled: false,
                    uniqueId: component?.uniqueId
                },
                position: { x: 150, y: 250 + ((index + 1) * 100) },
                type: 'custom',
            }))
            const updatedBusComponents = busComponents.map((component, index) => ({
                id: `bus-${index + 1}`,
                data: {
                    label: component?.name,
                    category: component?.category,
                    icon: <BusCompSvg />,
                    handlers: [
                        {
                            id: 'right',
                            type: 'target' as HandleType,
                            position: Position.Right,
                        }
                    ],
                    disabled: false,
                    uniqueId: component?.uniqueId
                },
                position: { x: 450, y: 250 + ((index + 1) * 100) },
                type: 'custom',
            }))

            getRemoteAppConnectionStatus([...defaultComponents, ...updatedPayloadComponents, ...updatedBusComponents])
        } catch (error) {
            console.log(error);
            showToastMessage("error", "Error fetching components")
            setRemoteAppLoader(false)
        }
    }

    const getAppStatus = async (
        components: RemoteNodeType[],
        reconnect: boolean
    ): Promise<{ connected: boolean; disconnected: boolean; timeout: boolean }> => {
        try {
            const response = await SimulateService.getHwilConnectStatus(mission_id, scenario_id, reconnect);
            const remote_applications = response?.data?.data;
            if (!remote_applications) return { connected: false, disconnected: true, timeout: false };
            const connected = remote_applications.every(obj => obj.status === 'CONNECTED');
            const timeout = remote_applications.some(obj => obj.status === 'TIMEOUT');
            const disconnected = !connected && !timeout;

            setRemoteConnectionStatus(remote_applications);
            setRemoteComponents(components, remote_applications)

            if (timeout && connectionIntervalRef.current) {
                clearTimeout(connectionIntervalRef.current);
            }
            return { connected, disconnected, timeout };
        } catch (error) {
            if (connectionIntervalRef.current) {
                clearTimeout(connectionIntervalRef.current);
            }
            setRemoteComponents(components, [])

            return { connected: false, disconnected: false, timeout: true };
        } finally {
            setRemoteAppLoader(false)
        }
    }

    // TODO: Temporary solution — currently using polling to check remote app connection status.
    // This should be replaced with a WebSocket-based implementation as per AT-9947:
    // https://antaris.atlassian.net/browse/AT-9947
    const getRemoteAppConnectionStatus = async (components: RemoteNodeType[]) => {
        if (!mission_id || !scenario_id) return;
        const result = await getAppStatus(components, true);
        if (result.timeout) return;
        const pollStatus = async () => {
            await getAppStatus(components, false);
            if (connectionIntervalRef.current) {
                connectionIntervalRef.current = setTimeout(pollStatus, 60000);
            }
        };
        connectionIntervalRef.current = setTimeout(pollStatus, 60000);
    };


    useEffect(() => {
        if (!scenario || !selected_satellite) return
        getAllRemoteComponents();
        return () => {
            if (connectionIntervalRef.current) {
                clearTimeout(connectionIntervalRef.current);
                connectionIntervalRef.current = null;
            }
        };
    }, [scenario, selected_satellite])

    return children
}

export default HwilProvider